import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useMinecraftStatus } from '../context/MinecraftContext';
import { useStore } from '../context/StoreContext';
import { useAppAuth } from '../context/AuthContext';
import './Navbar.css';

/* ── Minecraft color code map ── */
const MC_COLORS = {
  '0': '#000000', '1': '#0000AA', '2': '#00AA00', '3': '#00AAAA',
  '4': '#AA0000', '5': '#AA00AA', '6': '#FFAA00', '7': '#AAAAAA',
  '8': '#555555', '9': '#5555FF', 'a': '#55FF55', 'b': '#55FFFF',
  'c': '#FF5555', 'd': '#FF55FF', 'e': '#FFFF55', 'f': '#FFFFFF',
};

function parseMinecraftCodes(text) {
  if (!text) return null;
  const spans = [];
  let color = '#AAAAAA';
  let bold = false;
  let buf = '';
  let i = 0;

  const flush = () => {
    if (buf) {
      spans.push(
        <span key={spans.length} style={{ color, fontWeight: bold ? 'bold' : 'normal' }}>{buf}</span>
      );
      buf = '';
    }
  };

  while (i < text.length) {
    if (text[i] === '<' && text[i + 1] === '#' && i + 8 < text.length && text[i + 8] === '>') {
      flush();
      color = text.slice(i + 1, i + 8);
      i += 9;
      continue;
    }
    if ((text[i] === '&' || text[i] === '\u00a7') && i + 1 < text.length) {
      const code = text[i + 1].toLowerCase();
      if (MC_COLORS[code]) { flush(); color = MC_COLORS[code]; i += 2; continue; }
      if (code === 'l') { flush(); bold = true; i += 2; continue; }
      if (code === 'r') { flush(); color = '#AAAAAA'; bold = false; i += 2; continue; }
      // skip other formatting codes
      if ('omnk'.includes(code)) { i += 2; continue; }
    }
    buf += text[i];
    i++;
  }
  flush();
  return spans.length > 0 ? spans : text;
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPlayPopup, setShowPlayPopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { serverStatus } = useMinecraftStatus();
  const { cartCount, setCartOpen, minecraftUsername, setMinecraftUsername } = useStore();
  const { isLoaded, isSignedIn, minecraftUuid, minecraftUsername: authMcUsername, hasRole, getToken } = useAppAuth();
  const { signOut } = useClerk();
  const location = useLocation();
  // Store temporarily disabled — restore the original line below to bring back the cart UI on /shop.
  // const isShopPage = location.pathname === '/shop';
  const isShopPage = false;
  const isAdminPage = location.pathname === '/panel' || location.pathname.startsWith('/panel/');
  const isLiveEventPage = location.pathname.startsWith('/event/');
  const isVotePage = location.pathname.startsWith('/vote/');

  // On admin page, resolve MC profile from auth or sessionStorage (fresh link)
  const adminMcProfile = (() => {
    if (!isAdminPage || !isSignedIn || !hasRole('moderator')) return null;
    if (minecraftUuid && authMcUsername) return { uuid: minecraftUuid, username: authMcUsername };
    try {
      const stored = sessionStorage.getItem('mc_linked_profile');
      if (stored) return JSON.parse(stored);
    } catch {}
    return null;
  })();

  const [prefix, setPrefix] = useState(null);

  const fetchPrefix = useCallback(async () => {
    if (!adminMcProfile?.uuid) return;
    try {
      const token = await getToken();
      const res = await fetch(`/api/minecraft?action=player-prefix&uuid=${encodeURIComponent(adminMcProfile.uuid)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPrefix(data.prefix);
      }
    } catch {}
  }, [adminMcProfile?.uuid, getToken]);

  useEffect(() => { fetchPrefix(); }, [fetchPrefix]);

  // No navbar on live event or vote pages
  if (isLiveEventPage || isVotePage) return null;

  // On panel pages, don't render anything until auth is resolved to prevent flicker
  if (isAdminPage && !isLoaded) return null;

  const handlePlayClick = () => {
    setShowPlayPopup(true);
    setIsClosing(false);
    navigator.clipboard.writeText('thetopia.net');
    setCopied(true);
  };

  const handleClosePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowPlayPopup(false);
      setIsClosing(false);
      setCopied(false);
    }, 300);
  };

  /* ── Admin navbar ── */
  if (adminMcProfile) {
    return (
      <nav className="navbar navbar-admin">
        <div className="navbar-admin-bar">
          <div className="navbar-admin-left">
            <Link to="/" className="navbar-admin-exit" title="Back to site">
              <i className="mdi mdi-arrow-left"></i>
            </Link>
            <Link to="/panel" className={`navbar-admin-tab${location.pathname === '/panel' ? ' navbar-admin-tab-active' : ''}`}>
              Overview
            </Link>
            <Link to="/panel/event-management" className={`navbar-admin-tab${location.pathname === '/panel/event-management' ? ' navbar-admin-tab-active' : ''}`}>
              Event Management
            </Link>
            <Link to="/panel/partykit" className={`navbar-admin-tab${location.pathname === '/panel/partykit' ? ' navbar-admin-tab-active' : ''}`}>
              PartyKit Health
            </Link>
            <Link to="/panel/thumbnails" className={`navbar-admin-tab${location.pathname === '/panel/thumbnails' ? ' navbar-admin-tab-active' : ''}`}>
              Thumbnails
            </Link>
            {location.pathname.startsWith('/panel/event-management/control/') && (
              <span className="navbar-admin-tab navbar-admin-tab-active">
                Control Panel
              </span>
            )}
          </div>
          <div className="navbar-admin-profile-box">
            <img
              src={`https://mc-heads.net/avatar/${adminMcProfile.uuid}/40`}
              alt={adminMcProfile.username}
              className="navbar-admin-avatar"
            />
            <div className="navbar-admin-identity">
              <span className="navbar-admin-mc-name">
                {prefix && <>{parseMinecraftCodes(prefix)}{' '}</>}
                {adminMcProfile.username}
              </span>
            </div>
            <button
              className="navbar-admin-signout"
              onClick={() => signOut({ redirectUrl: '/panel' })}
              title="Sign out"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar">
      <div className="navbar-top">
        <Link to="/" className="navbar-logo-mobile">
          <img src="/text_logo.png" alt="TheTopia Logo" />
        </Link>
        <div className="navbar-social-bubbles">
          <a href="https://discord.thetopia.net" className="social-bubble discord-bubble" target="_blank" rel="noopener noreferrer">
            <svg className="discord-logo" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.25-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12z"/>
            </svg>
          </a>
        </div>
        {isShopPage && minecraftUsername && (
          <button
            className="navbar-user-bubble"
            onClick={() => setMinecraftUsername('')}
            title="Click to sign out"
          >
            <img
              src={`https://mc-heads.net/avatar/${minecraftUsername}/24`}
              alt={minecraftUsername}
              className="navbar-user-avatar"
            />
            <span className="navbar-user-name">{minecraftUsername}</span>
            <i className="mdi mdi-logout-variant navbar-logout-icon"></i>
          </button>
        )}
      </div>

      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src="/logo.png" alt="TheTopia Logo" />
        </Link>

        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={`navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <NavLink to="" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Home</NavLink>
          <span className="nav-separator">•</span>
          <NavLink to="/creators" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Creators</NavLink>
          <span className="nav-separator">•</span>
          <NavLink to="/how-to-join" className="nav-link" onClick={() => setMobileMenuOpen(false)}>How to Join</NavLink>
          <span className="nav-separator">•</span>
          <NavLink to="/about" className="nav-link" onClick={() => setMobileMenuOpen(false)}>About</NavLink>
        </div>

        <div className="navbar-actions">
          <div className="play-btn-wrapper">
            <button className="btn btn-outline btn-nav" onClick={handlePlayClick}>
              <i className="mdi mdi-play"></i> Play
            </button>
            <span className={`player-count-badge ${serverStatus.online ? 'online' : 'offline'}`}>
              {serverStatus.players.online}
            </span>
          </div>
          {isShopPage ? (
            <button className="btn btn-secondary btn-nav btn-shop-cart" onClick={() => setCartOpen(true)}>
              <i className="mdi mdi-cart"></i> Cart
              {cartCount > 0 && (
                <span className="cart-count-badge">{cartCount}</span>
              )}
            </button>
          ) : (
            <NavLink to="/shop" className="btn btn-secondary btn-nav btn-shop-cart" onClick={() => setMobileMenuOpen(false)}>
              <i className="mdi mdi-cart"></i> Shop
            </NavLink>
          )}
        </div>
      </div>

      {showPlayPopup && createPortal(
        <div className={`play-popup-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClosePopup}>
          <div className={`play-popup ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="play-popup-content">
              <h2 className="server-ip">thetopia.net</h2>
              {copied && <p className="copied-message">✓ Copied to clipboard!</p>}
              <div className="server-instructions">
                <h3>How to Join:</h3>
                <ol>
                  <li>Open Minecraft</li>
                  <li>Click "Multiplayer"</li>
                  <li>Click "Add Server"</li>
                  <li>Paste the server address</li>
                  <li>Click "Done" and join!</li>
                </ol>
              </div>
              <button className="close-popup-btn" onClick={handleClosePopup}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
}
